import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Flex, Text } from '@chakra-ui/core'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import { RootState } from 'constants/interfaces'
import { CloseButton } from 'components/ui'
import LocaleText from 'components/LocaleText'
import { NotificationServiceIcon } from 'components/Notifications/NotificationServiceIcon'
import { useDateLocale } from 'hooks/useDateLocale'
import { notifShadows, notifTokens } from 'services/notifications/designTokens'
import { getNotificationVisual } from 'services/notifications/serviceVisuals'
import { INotification } from 'services/notifications/types'
import { resolveNotificationTitle } from 'services/notifications/notificationTitle'
import { formatNotificationRowDate } from 'services/notifications/formatNotificationTime'

const DURATION_S = 8

const slideIn = keyframes`
  from { transform: translateX(24px) scale(0.97); opacity: 0; }
  to { transform: translateX(0) scale(1); opacity: 1; }
`
const slideOut = keyframes`
  from { transform: translateX(0) scale(1); opacity: 1; }
  to { transform: translateX(24px) scale(0.97); opacity: 0; }
`
const slideInRtl = keyframes`
  from { transform: translateX(-24px) scale(0.97); opacity: 0; }
  to { transform: translateX(0) scale(1); opacity: 1; }
`
const slideOutRtl = keyframes`
  from { transform: translateX(0) scale(1); opacity: 1; }
  to { transform: translateX(-24px) scale(0.97); opacity: 0; }
`
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`
const shrinkWidth = keyframes`
  from { width: 100%; }
  to { width: 0%; }
`

const Card = styled(Box)<{ isExiting: boolean; depthTransform: string; isRtl: boolean }>`
  transform: ${({ depthTransform }) => depthTransform};
  animation: ${({ isExiting, isRtl }) => (isExiting ? (isRtl ? slideOutRtl : slideOut) : isRtl ? slideInRtl : slideIn)}
    ${({ isExiting }) => (isExiting ? '160ms' : '220ms')} ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-name: ${({ isExiting }) => (isExiting ? fadeOut : fadeIn)};
    animation-duration: 120ms;
  }
`

// Only the front card's bar actually counts down (animation-play-state toggles between paused/
// running) — a card that isn't front sits frozen at 100% since its animation never started, and
// one bumped out of front mid-countdown freezes at whatever width it had reached.
const TimerBar = styled(Box)<{ isRunning: boolean }>`
  animation: ${shrinkWidth} ${DURATION_S}s linear forwards;
  animation-play-state: ${({ isRunning }) => (isRunning ? 'running' : 'paused')};

  @media (prefers-reduced-motion: reduce) {
    animation-play-state: ${({ isRunning }) => (isRunning ? 'running' : 'paused')};
  }
`

interface IProps {
  notification: INotification
  isExiting: boolean
  isFront: boolean
  depthTransform: string
  onClick: () => void
  onDismiss: () => void
  isRtl?: boolean
}

// Bottom-right toast for a push that arrived while the tab was open and focused (see
// usePushNotificationMessages / push-sw.js's respondToNotification). Only the front card's 8s
// timer runs; hovering it pauses (native animation-play-state — resumes from the same point,
// doesn't restart).
export const PushToastCard: React.FC<IProps> = ({
  notification,
  isExiting,
  isFront,
  depthTransform,
  onClick,
  onDismiss,
  isRtl = false,
}) => {
  const visual = getNotificationVisual(notification, isRtl)
  const locale = useSelector((state: RootState) => state.general.locale)
  const translations = useSelector((state: RootState) => state.config.translations)
  // Registers dayjs's locale data file so formatNotificationRowDate below actually translates the
  // month name instead of silently falling back to English — same fix as NotificationRow.
  useDateLocale(locale)
  const serviceLabel = translations?.[locale]?.[visual.label] ?? visual.label
  const title = resolveNotificationTitle(notification, translations?.[locale] as Record<string, string>, serviceLabel)
  const isUnread = !notification.seen
  const serviceColor = isUnread ? visual.text : notifTokens.faint
  const [isHovered, setIsHovered] = useState(false)

  return (
    <Card
      isExiting={isExiting}
      depthTransform={depthTransform}
      isRtl={isRtl}
      onClick={onClick}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      aria-label={title}
      cursor="pointer"
      bg="white"
      borderRadius="12px"
      border={`1px solid ${notifTokens.border}`}
      boxShadow={notifShadows.toast}
      w="352px"
      overflow="hidden"
      position="relative"
      transformOrigin={isRtl ? 'left bottom' : 'right bottom'}
    >
      <Flex p="13px 13px 15px" alignItems="flex-start" style={{ columnGap: '16px' }}>
        <NotificationServiceIcon visual={visual} isUnread={isUnread} senderId={notification.sender_id} isRtl={isRtl} />
        <Box flex="1" minW="0" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Flex alignItems="center" style={{ columnGap: '5px' }}>
            <Text fontSize="11.5px" fontWeight={600} color={serviceColor}>
              <LocaleText text={visual.label} />
            </Text>
            <Text fontSize="11.5px" fontWeight={600} color={notifTokens.faint}>
              ·
            </Text>
            <Text fontSize="11.5px" color={notifTokens.faint} style={{ unicodeBidi: 'plaintext' }}>
              {formatNotificationRowDate(notification.created_at_ts, locale)}
            </Text>
          </Flex>

          <Text
            fontWeight={600}
            fontSize="13.5px"
            color={notifTokens.ink}
            isTruncated
            textAlign={isRtl ? 'right' : 'left'}
            style={{ unicodeBidi: 'plaintext' }}
          >
            {title}
          </Text>
          {notification.body && (
            <Text
              fontSize="12.5px"
              lineHeight="18px"
              color={notifTokens.muted}
              textAlign={isRtl ? 'right' : 'left'}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                unicodeBidi: 'plaintext',
              }}
            >
              {notification.body}
            </Text>
          )}
        </Box>
        <CloseButton
          minW="22px"
          h="22px"
          p="3px"
          color={notifTokens.disabled}
          _hover={{ bg: '#F1F5F9', color: notifTokens.ink }}
          _active={{ bg: '#F1F5F9', transform: 'none' }}
          _focus={{ boxShadow: 'none', outline: 'none' }}
          onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation()
            onDismiss()
          }}
        />
      </Flex>
      <TimerBar
        isRunning={isFront && !isHovered && !isExiting}
        onAnimationEnd={onDismiss}
        position="absolute"
        bottom="0"
        left="0"
        h="2px"
        bg={visual.text}
        opacity={0.5}
      />
    </Card>
  )
}
